import { clamp } from './math';

/**
 * One Euro filter: an adaptive low-pass filter that trades jitter for lag based
 * on the signal's speed. Used to smooth MediaPipe head-pose samples.
 * Reference: Casiez, Roussel & Vogel, CHI 2012.
 */
export class OneEuroFilter {
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;
  private xPrev: number | null = null;
  private dxPrev = 0;

  constructor(options: { minCutoff?: number; beta?: number; dCutoff?: number } = {}) {
    this.minCutoff = options.minCutoff ?? 1.0;
    this.beta = options.beta ?? 0.007;
    this.dCutoff = options.dCutoff ?? 1.0;
  }

  private static alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(value: number, dt: number): number {
    const step = Math.max(dt, 1e-3);
    if (this.xPrev === null) {
      this.xPrev = value;
      this.dxPrev = 0;
      return value;
    }

    const dx = (value - this.xPrev) / step;
    const aD = OneEuroFilter.alpha(this.dCutoff, step);
    const dxHat = aD * dx + (1 - aD) * this.dxPrev;

    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const a = OneEuroFilter.alpha(cutoff, step);
    const xHat = a * value + (1 - a) * this.xPrev;

    this.xPrev = xHat;
    this.dxPrev = dxHat;
    return xHat;
  }

  reset(): void {
    this.xPrev = null;
    this.dxPrev = 0;
  }
}

/**
 * Constant-velocity Kalman filter for one scalar signal.
 *
 * The state is `[position, velocity]`; the process model is constant velocity
 * with white acceleration noise, and each measurement is the position with a
 * fixed variance. Carrying velocity means a steady lean is tracked with much
 * less lag than a plain low-pass gives at the same jitter rejection, while a
 * single corrupt sample cannot snap the output: the innovation is gated to a
 * few of its own standard deviations, so a blink or a tracking glitch is
 * swallowed instead of yanking the window. It runs on variable `dt`, which is
 * what the rate-limited tracker hands it. It replaces the One Euro filter as
 * the default head-pose smoother (the One Euro stays for `?leanlegacy`).
 */
export class KalmanFilter1D {
  private x = 0;
  private v = 0;
  /** Symmetric 2x2 covariance, stored as p00, p01 = p10, p11. */
  private p00 = 1;
  private p01 = 0;
  private p11 = 1;
  private initialized = false;

  constructor(
    /** Acceleration noise spectral density (m^2/s^3); higher tracks faster. */
    private readonly processNoise: number,
    /** Measurement variance (m^2); higher trusts the filter over the sample. */
    private readonly measurementNoise: number,
    /** Innovation gate, in standard deviations, for outlier rejection. */
    private readonly gate = 3,
  ) {}

  filter(measurement: number, dt: number): number {
    const step = Math.max(dt, 1e-3);
    if (!this.initialized) {
      this.reset(measurement);
      return this.x;
    }

    // Predict: x += v*dt, and the covariance grows by the process noise.
    this.x += this.v * step;
    const q = this.processNoise;
    const p00 = this.p00 + step * (2 * this.p01 + step * this.p11) + (q * step * step * step) / 3;
    const p01 = this.p01 + step * this.p11 + (q * step * step) / 2;
    const p11 = this.p11 + q * step;

    // Update. Gate the innovation so a glitch cannot yank the estimate.
    const residual = measurement - this.x;
    const variance = p00 + this.measurementNoise;
    const bound = this.gate * Math.sqrt(variance);
    const gated = Math.abs(residual) > bound ? Math.sign(residual) * bound : residual;
    const k0 = p00 / variance;
    const k1 = p01 / variance;
    this.x += k0 * gated;
    this.v += k1 * gated;
    this.p00 = (1 - k0) * p00;
    this.p01 = (1 - k0) * p01;
    this.p11 = p11 - k1 * p01;

    return this.x;
  }

  /** Re-seed the state at `seed`, or clear it to re-seed on the next sample. */
  reset(seed?: number): void {
    this.v = 0;
    // A small initial covariance: the seed is treated as a good sample, so the
    // filter is already smoothing from the first update rather than trusting
    // the first few measurements whole.
    this.p00 = this.measurementNoise;
    this.p01 = 0;
    this.p11 = this.measurementNoise;
    if (seed === undefined) {
      this.initialized = false;
      return;
    }
    this.x = seed;
    this.initialized = true;
  }
}

/** A plain exponential smoother. */
export class LowPassFilter {
  private value: number | null = null;

  constructor(private readonly smoothing: number) {}

  filter(input: number, dt: number): number {
    const alpha = 1 - Math.exp((-dt * this.smoothing) / 1);
    if (this.value === null) {
      this.value = input;
      return input;
    }
    this.value = alpha * input + (1 - alpha) * this.value;
    return this.value;
  }

  reset(): void {
    this.value = null;
  }
}

/**
 * Symmetric dead zone: small inputs are suppressed, larger inputs are shifted so
 * the output starts from zero at the edge of the zone.
 */
export class DeadZone {
  constructor(public readonly deadZone: number) {}

  apply(value: number): number {
    const magnitude = Math.abs(value);
    if (magnitude <= this.deadZone) return 0;
    const sign = Math.sign(value);
    return sign * (magnitude - this.deadZone);
  }
}

/** Clamp a value into a symmetric range. */
export function clampSymmetric(value: number, limit: number): number {
  return clamp(value, -limit, limit);
}
